import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useField } from '@unform/core';
import { NcmService } from '../services/api/cadastros/NcmService';

type TAutoCompleteOption = {
  ncm: string;
  label: string;
}
interface IAutoCompleteNcmProps {
  isExternalLoading?: boolean;
  ncms?: string;
}
// Mesmo padrão do AutoCompleteCfop.tsx, com uma diferença: o código do NCM é
// mantido como string (não Number) porque começa com zero em vários capítulos
// (ex: "01012100") — convertendo pra number essa casa perderia o zero à esquerda.
export const AutoCompleteNcm: React.FC<IAutoCompleteNcmProps> = ({ isExternalLoading = false, ncms = '' }) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField('ncm');
  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  // Mesma corrida de buscas explicada em AutoCompleteCfop.tsx: `cancelado`
  // garante que só a resposta da busca mais recente atualiza `opcoes`.
  useEffect(() => {
    let cancelado = false;
    setIsLoading(true);
    NcmService.getAlllist(busca, selectedId)
      .then((result) => {
        if (cancelado) return;
        setIsLoading(false);

        if (result instanceof Error) {
          // alert(result.message);
        } else {
          setOpcoes(result.data.map(ncm => ({ ncm: ncm.ncm, label: ncm.ncm + ' - ' + ncm.descricao })));
        }
      });
    return () => { cancelado = true; };
  }, [busca, selectedId]);

  useEffect(() => {
    if (ncms !== '') {
      let cancelado = false;
      setIsLoading(true);
      NcmService.getAlllist(ncms)
        .then((result) => {
          if (cancelado) return;
          setIsLoading(false);

          if (result instanceof Error) {
            // alert(result.message);
          } else {
            setOpcoes(result.data.map(ncm => ({ ncm: ncm.ncm, label: ncm.ncm + ' - ' + ncm.descricao })));
          }
        });
      setSelectedId(ncms);
      return () => { cancelado = true; };
    }
  }, [ncms]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.ncm === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);


  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'

      disablePortal

      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading}
      value={autoCompleteSelectedOption}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setBusca(newValue);
      }}
      onChange={(_, newValue) => { setSelectedId(newValue?.ncm); setBusca(''); clearError(); }}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          label="NCM"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};
